import { PartFinderImageKind, PartFinderReport, ThumbnailMatchType } from '@/api/generatedApi';
import { useGetPartFinderReportThumbnail } from '@/hooks/api/part-finder-reports/useGetPartFinderReportThumbnail';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { PartFinderThumbnailHover, ThumbnailFrame } from './PartFinderThumbnailHover';

const WBPARTS_ATTRIBUTION = '© WBParts';
const RELATED_CAPTION = `Related product — not the searched NSN · ${WBPARTS_ATTRIBUTION}`;
const SEARCH_PHOTO_CAPTION = 'The photo you searched with';

const thumbnailCaption = (report: PartFinderReport): string => {
  if (report.thumbnailKind === PartFinderImageKind.SearchPhoto) return SEARCH_PHOTO_CAPTION;
  return report.thumbnailMatchType === ThumbnailMatchType.Related ? RELATED_CAPTION : WBPARTS_ATTRIBUTION;
};

const RelatedBadge = styled('span')(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  right: 0,
  minWidth: '12px',
  height: '12px',
  padding: '0 2px',
  fontSize: '10px',
  lineHeight: '12px',
  textAlign: 'center',
  fontWeight: 700,
  color: theme.palette.common.white,
  backgroundColor: theme.palette.text.secondary,
  borderTopLeftRadius: '4px',
}));

interface PartFinderThumbnailCellProperties {
  report: PartFinderReport;
}

export const PartFinderThumbnailCell = ({ report }: PartFinderThumbnailCellProperties) => {
  const isRelated = report.thumbnailMatchType === ThumbnailMatchType.Related;
  const [hasError, setHasError] = useState(false);

  const { thumbnail, isLoading } = useGetPartFinderReportThumbnail({
    reportId: report.id,
    version: report.updatedAt,
    enabled: report.hasThumbnail,
  });

  useEffect(() => {
    setHasError(false);
  }, [report.id, report.updatedAt]);

  if (!report.hasThumbnail || hasError || (!isLoading && !thumbnail)) {
    return <></>;
  }

  if (isLoading || !thumbnail) {
    return <ThumbnailFrame />;
  }

  const alt = `${report.partNumber} thumbnail`;
  return (
    <PartFinderThumbnailHover thumbnailUrl={thumbnail} alt={alt} caption={thumbnailCaption(report)}>
      <ThumbnailFrame>
        <img
          src={thumbnail}
          alt={alt}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setHasError(true)}
        />
        {isRelated && <RelatedBadge>~</RelatedBadge>}
      </ThumbnailFrame>
    </PartFinderThumbnailHover>
  );
};
